/**
 * API для работы с диаграммами через backend
 * Интеграция с meta-registry-service
 */

import { API_ENDPOINTS, apiRequest } from '../config/api';

// Моковый userId до внедрения авторизации
const MOCK_USER_ID = '00000000-0000-0000-0000-000000000001';

/**
 * Получить текущий userId (пока моковый, позже будет из контекста авторизации)
 * @returns {string}
 */
const getCurrentUserId = () => {
  // TODO: После внедрения авторизации, получать из auth context
  return MOCK_USER_ID;
};

/**
 * Преобразовать данные от backend в формат frontend
 * @param {Object} backendData - данные от backend
 * @returns {Object} данные в формате frontend
 */
const mapBackendToFrontend = (backendData) => {
  return {
    id: backendData.doc_id,
    userId: backendData.user_id,
    meta: backendData.meta,
    view: backendData.view,
    elements: backendData.elements || [],
    createdAt: backendData.created_at,
    updatedAt: backendData.updated_at,
    code: backendData.meta?.code || '',
    gitlab_path: backendData.gitlab_path || null,
    sync_status: backendData.sync_status || backendData.meta?.sync_status || null,
  };
};

/**
 * Преобразовать данные от frontend в формат backend
 * @param {Object} frontendData - данные от frontend
 * @param {string} userId - ID пользователя
 * @returns {Object} данные в формате backend
 */
const mapFrontendToBackend = (frontendData, userId) => {
  const backendData = {
    user_id: userId || getCurrentUserId(),
    meta: {
      ...frontendData.meta,
      code: frontendData.meta?.code || frontendData.code || ''
    },
    view: frontendData.view,
    elements: frontendData.elements || []
  };
  const syncStatus = frontendData.sync_status || frontendData.meta?.sync_status;
  if (syncStatus) {
    backendData.sync_status = syncStatus;
  }
  return backendData;
};

/**
 * Получить список всех диаграмм
 * @param {string} projectId - ID пространства
 * @returns {Promise<Array>} массив диаграмм
 */
export const getDiagrams = async (projectId) => {
  try {
    const diagrams = await apiRequest(API_ENDPOINTS.diagrams.list(projectId), {
      method: 'GET',
    });

    // Преобразуем каждую диаграмму из формата backend в формат frontend
    const mappedDiagrams = (diagrams || []).map(mapBackendToFrontend);

    return mappedDiagrams;
  } catch (error) {
    console.error('Ошибка при получении списка диаграмм:', error);
    throw error;
  }
};

/**
 * Получить диаграмму по ID
 * @param {string} projectId - ID пространства
 * @param {string} id - ID диаграммы (doc_id)
 * @returns {Promise<Object>} данные диаграммы
 */
export const getDiagramById = async (projectId, id) => {
  try {
    const diagram = await apiRequest(API_ENDPOINTS.diagrams.getById(projectId, id), {
      method: 'GET',
    });

    // Преобразуем из формата backend в формат frontend
    return mapBackendToFrontend(diagram);
  } catch (error) {
    console.error(`Ошибка при получении диаграммы ${id}:`, error);
    throw error;
  }
};

/**
 * Создать новую диаграмму
 * @param {string} projectId - ID пространства
 * @param {Object} diagramData - данные диаграммы
 * @param {Object} diagramData.meta - метаданные (name, version, domain, etc.)
 * @param {Object} diagramData.view - параметры отображения (zoom, scroll)
 * @param {Array} diagramData.elements - элементы диаграммы
 * @returns {Promise<Object>} созданная диаграмма с ID
 */
export const createDiagram = async (projectId, diagramData) => {
  try {
    // Подготавливаем данные в формате frontend
    const frontendData = {
      meta: {
        name: diagramData.meta?.name || 'Новая диаграмма',
        version: diagramData.meta?.version || 1,
        createdAt: diagramData.meta?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        domain: diagramData.meta?.domain || 'Architecture Vision',
        ...diagramData.meta,
        code: diagramData.meta?.code || diagramData.code || '',
      },
      view: diagramData.view || {
        zoom: 1,
        scroll: { left: 0, top: 0 },
      },
      elements: diagramData.elements || [],
      sync_status: diagramData.sync_status || diagramData.meta?.sync_status,
    };

    // Преобразуем в формат backend
    const backendPayload = mapFrontendToBackend(frontendData, getCurrentUserId());
    if (diagramData.png_preview) {
      backendPayload.png_preview = diagramData.png_preview;
    }

    const result = await apiRequest(API_ENDPOINTS.diagrams.create(projectId), {
      method: 'POST',
      body: JSON.stringify(backendPayload),
    });

    // Преобразуем ответ обратно в формат frontend
    return mapBackendToFrontend(result);
  } catch (error) {
    console.error('Ошибка при создании диаграммы:', error);
    throw error;
  }
};

/**
 * Обновить существующую диаграмму
 * @param {string} projectId - ID пространства
 * @param {string} id - ID диаграммы (doc_id)
 * @param {Object} diagramData - обновленные данные диаграммы
 * @returns {Promise<Object>} обновленная диаграмма
 */
export const updateDiagram = async (projectId, id, diagramData) => {
  try {
    // Обновляем updatedAt и инкрементируем version
    const frontendData = {
      ...diagramData,
      sync_status: diagramData.sync_status || diagramData.meta?.sync_status,
      meta: {
        ...diagramData.meta,
        updatedAt: new Date().toISOString(),
        version: (diagramData.meta?.version || 1) + 1,
      },
    };

    // Преобразуем в формат backend
    const backendPayload = mapFrontendToBackend(
      frontendData,
      diagramData.userId || getCurrentUserId()
    );
    if (diagramData.png_preview) {
      backendPayload.png_preview = diagramData.png_preview;
    }

    const result = await apiRequest(API_ENDPOINTS.diagrams.update(projectId, id), {
      method: 'PUT',
      body: JSON.stringify(backendPayload),
    });

    // Преобразуем ответ обратно в формат frontend
    return mapBackendToFrontend(result);
  } catch (error) {
    console.error(`Ошибка при обновлении диаграммы ${id}:`, error);
    throw error;
  }
};

/**
 * Опубликовать диаграмму в GitLab
 * @param {string} projectId - ID пространства
 * @param {string} id - doc_id диаграммы
 * @param {{ rootEntryCode?: string, rootRegistrySlug?: string, pngPreview?: string }} [options]
 * @returns {Promise<void>}
 */
export const publishDiagram = async (projectId, id, options = {}) => {
  try {
    const body = {};
    if (options.rootEntryCode) body.root_entry_code = options.rootEntryCode;
    if (options.rootRegistrySlug) body.root_registry_slug = options.rootRegistrySlug;
    if (options.pngPreview) body.png_preview = options.pngPreview;
    if (options.fitnessOverride) body.fitness_override = true;
    return await apiRequest(API_ENDPOINTS.diagrams.publish(projectId, id), {
      method: 'POST',
      body: JSON.stringify(body),
    });
  } catch (error) {
    if (error?.payload?.error === 'fitness_warnings') {
      const fitnessError = new Error('fitness_warnings');
      fitnessError.isFitnessWarnings = true;
      fitnessError.warnings = Array.isArray(error.payload.warnings) ? error.payload.warnings : [];
      fitnessError.status = error.status;
      throw fitnessError;
    }
    console.error(`Ошибка при публикации диаграммы ${id}:`, error);
    throw error;
  }
};

/**
 * Удалить диаграмму
 * @param {string} projectId - ID пространства
 * @param {string} id - ID диаграммы (doc_id)
 * @returns {Promise<void>}
 */
export const deleteDiagram = async (projectId, id) => {
  try {
    await apiRequest(API_ENDPOINTS.diagrams.delete(projectId, id), {
      method: 'DELETE',
    });
  } catch (error) {
    console.error(`Ошибка при удалении диаграммы ${id}:`, error);
    throw error;
  }
};

/**
 * Resolve a diagram sync conflict by accepting the GitLab version.
 * Backend writes GitLab DSL into meta.bpmnDsl (BPMN) or meta.sourceCode (UML).
 * After this call, reload the diagram from DB and show new DSL in code panel.
 * @param {string} id - diagram doc_id
 */
export const resolveConflictAcceptGitlab = async (id) => {
  await apiRequest(`${API_ENDPOINTS.adminConflicts.acceptGitlab(id)}?entity_type=diagram`, {
    method: 'POST',
  });
};

/**
 * Resolve a diagram sync conflict by keeping the platform version.
 * Sets sync_status to "draft". Next publish will succeed because gitlab_sha
 * retains the remote SHA from conflict detection, which still matches remote HEAD.
 * @param {string} id - diagram doc_id
 */
export const resolveConflictAcceptPlatform = async (id) => {
  await apiRequest(`${API_ENDPOINTS.adminConflicts.acceptPlatform(id)}?entity_type=diagram`, {
    method: 'POST',
  });
};

/**
 * Экспортировать диаграмму в формате для сохранения
 * @param {Object} diagramState - текущее состояние диаграммы из редактора
 * @returns {Object} форматированные данные для API
 */
export const formatDiagramForExport = (diagramState) => {
  return {
    meta: {
      name: diagramState.name || 'Без названия',
      version: diagramState.version || 1,
      createdAt: diagramState.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      domain: diagramState.domain || 'Architecture Vision',
      code: diagramState.code || ''
    },
    view: {
      zoom: diagramState.zoom || 1,
      scroll: {
        left: diagramState.scrollLeft || 0,
        top: diagramState.scrollTop || 0,
      },
    },
    elements: diagramState.elements || [],
  };
};

/**
 * Импортировать диаграмму из формата API в состояние редактора
 * @param {Object} diagramData - данные диаграммы от API
 * @returns {Object} состояние для редактора
 */
export const formatDiagramForImport = (diagramData) => {
  return {
    id: diagramData.id,
    name: diagramData.meta?.name || 'Без названия',
    version: diagramData.meta?.version || 1,
    createdAt: diagramData.meta?.createdAt,
    updatedAt: diagramData.meta?.updatedAt,
    domain: diagramData.meta?.domain || 'Architecture Vision',
    zoom: diagramData.view?.zoom || 1,
    scrollLeft: diagramData.view?.scroll?.left || 0,
    scrollTop: diagramData.view?.scroll?.top || 0,
    elements: diagramData.elements || [],
    code: diagramData.meta?.code || ''
  };
};
